/**
 * TargetHistoryTable — Phase 2.2B Session 3
 *
 * Reads: GET /api/nutrition/targets/{athlete_id}
 * Append-only. No edit / no inline delete (admin DELETE endpoint exists but is hidden from UI
 * per Phase 2.2B scope — "Nuovo target = nuova versione").
 */
import React, { useCallback, useEffect, useState } from 'react';
import { History, Loader2, Sparkles, User } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';

const fmt0 = (v) => v === null || v === undefined ? '—' : Math.round(Number(v));
const fmt1 = (v) => v === null || v === undefined ? '—' : Number(v).toFixed(1);

const TargetHistoryTable = ({ athleteId, refreshKey }) => {
  const { api } = useAuth();
  const { language } = useLanguage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/nutrition/targets/${athleteId}`);
      setRows(res.data || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [api, athleteId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <Card className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid="target-history-table">
      <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-2">
        <History className="w-4 h-4 text-zinc-400" />
        <h3 className="text-sm font-semibold text-zinc-200">
          {language === 'IT' ? 'Storico target' : 'Target history'}
        </h3>
        <span className="text-xs text-zinc-500 ml-1">({rows.length})</span>
      </div>
      {loading ? (
        <div className="p-8 flex items-center justify-center text-zinc-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> {language === 'IT' ? 'Caricamento…' : 'Loading…'}
        </div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-zinc-500 text-sm" data-testid="target-history-empty">
          {language === 'IT' ? 'Nessun target registrato.' : 'No target recorded yet.'}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-zinc-950 text-zinc-400 uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left">{language === 'IT' ? 'Dal' : 'From'}</th>
                <th className="px-3 py-2 text-left">{language === 'IT' ? 'Al' : 'To'}</th>
                <th className="px-3 py-2 text-left">{language === 'IT' ? 'Obiettivo' : 'Goal'}</th>
                <th className="px-3 py-2 text-left">{language === 'IT' ? 'Sorgente' : 'Source'}</th>
                <th className="px-3 py-2 text-right">{language === 'IT' ? 'Calorie' : 'Calories'}</th>
                <th className="px-3 py-2 text-right">P</th>
                <th className="px-3 py-2 text-right">C</th>
                <th className="px-3 py-2 text-right">F</th>
                <th className="px-3 py-2 text-center">{language === 'IT' ? 'Stato' : 'Status'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {rows.map((r) => {
                const f = r.final || {};
                return (
                  <tr key={r.id} className="hover:bg-zinc-800/40" data-testid={`target-row-${r.id}`}>
                    <td className="px-3 py-2 text-zinc-200">{r.effective_from}</td>
                    <td className="px-3 py-2 text-zinc-500">{r.effective_to || '—'}</td>
                    <td className="px-3 py-2">
                      <Badge className="bg-zinc-800 text-zinc-200 border-zinc-700 text-[10px]">{r.goal}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      <Badge className={r.mode === 'AUTO'
                        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40 text-[10px]'
                        : 'bg-zinc-700 text-zinc-200 border-zinc-600 text-[10px]'}>
                        {r.mode === 'AUTO' ? <Sparkles className="w-2.5 h-2.5 mr-1 inline" /> : <User className="w-2.5 h-2.5 mr-1 inline" />}
                        {r.mode}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-right text-zinc-100 font-medium">{fmt0(f.calories)} <span className="text-zinc-500">kcal</span></td>
                    <td className="px-3 py-2 text-right text-zinc-300">{fmt1(f.protein_g)}</td>
                    <td className="px-3 py-2 text-right text-zinc-300">{fmt1(f.carbs_g)}</td>
                    <td className="px-3 py-2 text-right text-zinc-300">{fmt1(f.fats_g)}</td>
                    <td className="px-3 py-2 text-center">
                      {r.is_active ? (
                        <Badge className="bg-cyan-500/20 text-cyan-300 border-cyan-500/40 text-[10px]">
                          {language === 'IT' ? 'ATTIVO' : 'ACTIVE'}
                        </Badge>
                      ) : (
                        <span className="text-zinc-600 text-[10px]">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default TargetHistoryTable;
